<template>
  <div class="pdf-viewer">
    <div class="pdf-controls">
      <button class="btn btn-default" @click="previousPage" :disabled="currentPage === 1">
        <i class="fa fa-chevron-left"></i> <span class="btn-label">Previous</span>
      </button>
      <span class="page-info">
        <span class="page-info-full">Page {{ currentPage }} of {{ totalPages }}</span>
        <span class="page-info-compact">{{ currentPage }} / {{ totalPages }}</span>
      </span>
      <button class="btn btn-default" @click="nextPage" :disabled="currentPage === totalPages">
        <span class="btn-label">Next</span> <i class="fa fa-chevron-right"></i>
      </button>
      <div class="zoom-controls">
        <button class="btn btn-default" @click="zoomOut" :disabled="scale <= 0.5">
          <i class="fa fa-search-minus"></i>
        </button>
        <span class="zoom-info">{{ Math.round(scale * 100) }}%</span>
        <button class="btn btn-default" @click="zoomIn" :disabled="scale >= 2">
          <i class="fa fa-search-plus"></i>
        </button>
      </div>
    </div>
    <div class="pdf-container" ref="pdfContainer">
      <div class="pdf-wrapper">
        <canvas ref="pdfCanvas" class="pdf-canvas"></canvas>

        <div class="annotation-overlay" ref="annotationOverlay" :style="overlayStyle">
          <slot name="annotations"></slot>
        </div>
        <slot name="overlay"></slot>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { PDFService } from '../services/PDFService';

export default {
  name: 'PDFViewer',
  props: {
    pdfUrl: {
      type: String,
      default: null
    },
    pdfFile: {
      type: File,
      default: null
    }
  },
  data() {
    return {
      pdfService: null,
      pdfDoc: null,
      currentPage: 1,
      totalPages: 0,
      scale: 1.0,
      pageRendering: false,
      pageNumPending: null,
      currentPageProxy: null,
      canvasWidth: 0,
      canvasHeight: 0,
      initialScaleSet: false
    };
  },
  computed: {
    overlayStyle() {
      const canvas = this.$refs.pdfCanvas;
      if (!canvas) return {};

      return {
        width: `${canvas.width}px`,
        height: `${canvas.height}px`
      };
    }
  },
  watch: {
    pdfUrl(newUrl) {
      if (newUrl) {
        this.loadPDF();
      }
    },
    pdfFile(newFile) {
      if (newFile) {
        this.loadPDFFromFile();
      }
    }
  },
  mounted() {
    this.pdfService = new PDFService();
    if (this.pdfUrl) {
      this.loadPDF();
    } else if (this.pdfFile) {
      this.loadPDFFromFile();
    }
  },
  beforeDestroy() {
    if (this.pdfService) {
      this.pdfService.destroy();
    }
  },
  methods: {
    async loadPDF() {
      if (!this.pdfUrl || !this.pdfService) return;

      try {
        this.pdfDoc = await this.pdfService.loadPDF(this.pdfUrl);
        this.totalPages = this.pdfDoc.numPages;
        this.currentPage = 1;
        this.$emit('pdf-loaded', {
          totalPages: this.totalPages,
          pdfDoc: this.pdfDoc
        });
        await this.renderPage(1);
      } catch (error) {
        console.error('Error loading PDF:', error);
        this.$emit('error', error);
      }
    },

    async loadPDFFromFile() {
      if (!this.pdfFile || !this.pdfService) return;

      try {
        this.pdfDoc = await this.pdfService.loadPDFFromFile(this.pdfFile);
        this.totalPages = this.pdfDoc.numPages;
        this.currentPage = 1;
        this.$emit('pdf-loaded', {
          totalPages: this.totalPages,
          pdfDoc: this.pdfDoc
        });
        await this.renderPage(1);
      } catch (error) {
        console.error('Error loading PDF from file:', error);
        this.$emit('error', error);
      }
    },

    async renderPage(pageNum) {
      if (!this.pdfService || !this.pdfDoc) return;

      this.pageRendering = true;

      try {
        const page = await this.pdfService.getPage(pageNum);
        this.currentPageProxy = page;
        const canvas = this.$refs.pdfCanvas;

        // Auto-fit to width on first render
        if (!this.initialScaleSet) {
          this.scale = this.calculateFitToWidthScale(page);
          this.initialScaleSet = true;
          this.$emit('scale-change', this.scale);
        }

        await this.pdfService.renderPage(page, canvas, this.scale);

        // Update canvas dimensions for grid overlay
        this.canvasWidth = canvas.width;
        this.canvasHeight = canvas.height;

        this.pageRendering = false;
        if (this.pageNumPending !== null) {
          await this.renderPage(this.pageNumPending);
          this.pageNumPending = null;
        }

        this.$emit('page-rendered', {
          pageNum,
          scale: this.scale,
          page
        });
      } catch (error) {
        console.error('Error rendering page:', error);
        this.pageRendering = false;
        this.$emit('error', error);
      }
    },

    calculateFitToWidthScale(page) {
      const container = this.$refs.pdfContainer;
      if (!container) return 1.0;

      // Use the container's content-box width so we match whatever padding
      // the current breakpoint applies (0 on mobile, 32px on desktop).
      const style = window.getComputedStyle(container);
      const containerWidth = container.clientWidth
        - parseFloat(style.paddingLeft)
        - parseFloat(style.paddingRight);

      // Get the page's natural width at scale 1.0
      const viewport = page.getViewport({ scale: 1.0 });
      const pageWidth = viewport.width;

      // Calculate the scale to fit the width
      let fitScale = containerWidth / pageWidth;

      // Clamp between 0.5 and 2.0, round down to nearest 0.05 so the page
      // never lands a hair wider than the container (which would push the
      // flex layout into safe-center's flex-start fallback on first render).
      fitScale = Math.max(0.5, Math.min(2.0, fitScale));
      fitScale = Math.floor(fitScale * 20) / 20;

      return fitScale;
    },

    queueRenderPage(pageNum) {
      if (this.pageRendering) {
        this.pageNumPending = pageNum;
      } else {
        this.renderPage(pageNum);
      }
    },

    async previousPage() {
      if (this.currentPage <= 1) return;
      this.currentPage--;
      this.queueRenderPage(this.currentPage);
      this.$emit('page-change', this.currentPage);
    },

    async nextPage() {
      if (this.currentPage >= this.totalPages) return;
      this.currentPage++;
      this.queueRenderPage(this.currentPage);
      this.$emit('page-change', this.currentPage);
    },

    async zoomIn() {
      if (this.scale >= 2) return;
      this.scale = Math.min(this.scale + 0.25, 2);
      this.queueRenderPage(this.currentPage);
      this.$emit('scale-change', this.scale);
    },

    async zoomOut() {
      if (this.scale <= 0.5) return;
      this.scale = Math.max(this.scale - 0.25, 0.5);
      this.queueRenderPage(this.currentPage);
      this.$emit('scale-change', this.scale);
    },

    async goToPage(pageNum) {
      if (pageNum < 1 || pageNum > this.totalPages) return;
      this.currentPage = pageNum;
      this.queueRenderPage(this.currentPage);
      this.$emit('page-change', this.currentPage);
    }
  }
};
</script>

<style scoped>
/* DriveScout PDF Viewer - Clean Admin Theme */
.pdf-viewer {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #e8edf2;
  min-height: 0;  /* Allow flex child to shrink below content size */
}

.pdf-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background-color: #fff;
  border-bottom: 1px solid #e2e8f0;
}

.pdf-controls .btn {
  background-color: #fff;
  border: 1px solid #cbd5e0;
  color: #4a5568;
  transition: all 0.15s ease;
}

.pdf-controls .btn:hover:not(:disabled) {
  background-color: #f7fafc;
  border-color: #a0aec0;
}

.pdf-controls .btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-info {
  font-weight: 500;
  color: #2d3748;
  white-space: nowrap;
}

.page-info-compact {
  display: none;
}

.zoom-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

@media (max-width: 480px) {
  .pdf-controls {
    padding: 10px 8px;
  }

  .pdf-controls .btn-label {
    display: none;
  }

  .page-info-full {
    display: none;
  }

  .page-info-compact {
    display: inline;
  }

  .zoom-controls {
    gap: 6px;
  }

  .zoom-info {
    display: none;
  }
}

@media (max-width: 768px) {
  .pdf-viewer,
  .pdf-container {
    background-color: #fff;
  }

  .pdf-container {
    padding: 0;
  }
}

.zoom-info {
  min-width: 50px;
  text-align: center;
  color: #718096;
  font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
  font-size: 13px;
}

.pdf-container {
  flex: 1;
  overflow: auto;
  display: flex;
  justify-content: safe center;
  align-items: flex-start;
  padding: 0px;
  background-color: #e8edf2;
}

.pdf-wrapper {
  position: relative;
  display: inline-block;
}

.pdf-canvas {
  /* Clean paper shadow */
  box-shadow:
    0 2px 4px rgba(0, 0, 0, 0.08),
    0 4px 12px rgba(0, 0, 0, 0.06);
  background-color: white;
  display: block;
  border-radius: 2px;
  border: 1px solid #e2e8f0;
}

.annotation-overlay {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
}

.annotation-overlay>* {
  pointer-events: auto;
}
</style>
